import { DeleteOutline } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import { canApply, weekdayDatesInColumns, weekdayOnlyDates } from 'src/pages/TruckShifts/bulkSelection';

type Props = {
  truckCount: number;
  dateColumns: string[];
  selectedDates: Set<string>;
  onDatesChange: (dates: Set<string>) => void;
  patterns: ShiftPatternEntity[];
  activePatternId: number | null;
  onPatternChange: (patternId: number) => void;
  onAddPattern: () => void;
  onManagePatterns: () => void;
  applying: boolean;
  onApply: () => void;
  onRemove: () => void;
  removing: boolean;
  removeCount: number;
};

const WEEKDAYS: { dow: number; label: string; color?: string }[] = [
  { dow: 1, label: '月' }, { dow: 2, label: '火' }, { dow: 3, label: '水' },
  { dow: 4, label: '木' }, { dow: 5, label: '金' },
  { dow: 6, label: '土', color: '#1565c0' },
  { dow: 0, label: '日', color: '#c62828' },
];

// プルダウン表示用：各スロットの時刻（`/` 連結）＋出っぱなし(・N日)
const patternSummary = (p: ShiftPatternEntity): string => {
  const slots = [...p.slots]
    .sort((a, b) => a.slotOrder - b.slotOrder)
    .map((s) => `${s.startTime}-${s.endTime}`)
    .join(' / ');
  return p.multiDay ? `${slots}・${p.multiDayLength ?? 2}日` : slots;
};

export const SelectionBar: FC<Props> = ({
  truckCount, dateColumns, selectedDates, onDatesChange,
  patterns, activePatternId, onPatternChange, onAddPattern, onManagePatterns,
  applying, onApply, onRemove, removing, removeCount,
}) => {
  // 反映・解除のどちらか実行中は両ボタンを止める（同一選択範囲への /bulk と /bulk_removal の並行実行を防ぐ）。
  const bulkBusy = applying || removing;
  const ready = canApply(truckCount, selectedDates.size, activePatternId) && !bulkBusy;
  const selectedPattern = patterns.find((p) => p.id === activePatternId);
  const applyLabel = canApply(truckCount, selectedDates.size, activePatternId)
    ? `${truckCount}台 × ${selectedDates.size}日 に反映`
    : '車両・期間・便を選択';

  const toggleWeekday = (dow: number) => {
    const targets = weekdayDatesInColumns(dateColumns, dow);
    const allOn = targets.length > 0 && targets.every((d) => selectedDates.has(d));
    const next = new Set(selectedDates);
    targets.forEach((d) => { if (allOn) next.delete(d); else next.add(d); });
    onDatesChange(next);
  };

  return (
    <Stack spacing={1} sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', backgroundColor: '#f5f7fb' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" fontWeight={700}>① 車両（{truckCount}台）</Typography>
        <Typography color="text.disabled">›</Typography>

        <Typography variant="body2" fontWeight={700}>② 期間（{selectedDates.size}日）</Typography>
        <Stack direction="row" spacing={0.5}>
          <Button size="small" sx={{ minWidth: 0, px: 1, py: 0, fontSize: 11 }} onClick={() => onDatesChange(new Set(dateColumns))}>全日</Button>
          <Button size="small" sx={{ minWidth: 0, px: 1, py: 0, fontSize: 11 }} onClick={() => onDatesChange(new Set(weekdayOnlyDates(dateColumns)))}>平日</Button>
          <Button size="small" sx={{ minWidth: 0, px: 1, py: 0, fontSize: 11 }} onClick={() => onDatesChange(new Set())}>解除</Button>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {WEEKDAYS.map(({ dow, label, color }) => {
            const on = weekdayDatesInColumns(dateColumns, dow).length > 0
              && weekdayDatesInColumns(dateColumns, dow).every((d) => selectedDates.has(d));
            return (
              <Box
                key={dow}
                onClick={() => toggleWeekday(dow)}
                aria-label={`曜日 ${label}`}
                sx={{
                  width: 24, height: 24, borderRadius: '50%', cursor: 'pointer', userSelect: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                  border: on ? '2px solid #1976d2' : '1px solid #cbd5e1',
                  backgroundColor: on ? '#e3f2fd' : '#fff',
                  color: on ? '#1976d2' : (color ?? '#334155'),
                }}
              >
                {label}
              </Box>
            );
          })}
        </Stack>
        <Typography color="text.disabled">›</Typography>

        <Typography variant="body2" fontWeight={700}>③ 便</Typography>
        <Select
          size="small"
          displayEmpty
          value={activePatternId != null ? String(activePatternId) : ''}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '__add') { onAddPattern(); return; }
            if (v === '__manage') { onManagePatterns(); return; }
            if (v !== '') onPatternChange(Number(v));
          }}
          sx={{ minWidth: 260, height: 32, backgroundColor: '#fff', fontSize: 13 }}
          renderValue={(val) => {
            const p = patterns.find((x) => String(x.id) === val);
            if (!p) return <Typography variant="caption" color="text.secondary">便を選択</Typography>;
            return (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: p.color, flexShrink: 0 }} />
                <Typography variant="caption" fontWeight={700}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary">{patternSummary(p)}</Typography>
              </Stack>
            );
          }}
        >
          {patterns.map((p) => (
            <MenuItem key={p.id} value={String(p.id)} sx={{ fontSize: 13 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: p.color, flexShrink: 0 }} />
                <Typography variant="caption" fontWeight={700}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{patternSummary(p)}</Typography>
              </Stack>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem value="__add" sx={{ fontSize: 12, color: 'text.secondary' }}>便を追加</MenuItem>
          <MenuItem value="__manage" sx={{ fontSize: 12, color: 'text.secondary' }}>便を管理</MenuItem>
        </Select>
        <Typography color="text.disabled">›</Typography>

        <Box flexGrow={1} />

        <Tooltip title={removeCount > 0 ? `選択中の登録シフト ${removeCount}件 を一括解除` : '選択した勤務を解除'}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={truckCount === 0 || selectedDates.size === 0 || bulkBusy}
              onClick={onRemove}
              sx={{ borderRadius: 1 }}
            >
              <DeleteOutline fontSize="small" />
              {removeCount > 0 && (
                <Typography component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {removeCount}
                </Typography>
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Button variant="contained" size="medium" disabled={!ready} onClick={onApply}>{applyLabel}</Button>
      </Stack>

      {selectedPattern?.multiDay && (
        <Typography variant="caption" color="text.secondary">
          ※ 出っぱなしのため各選択日から{selectedPattern.multiDayLength ?? 2}日分を作成（同期間を上書き削除）します
        </Typography>
      )}
    </Stack>
  );
};
